<template>
  <div class="gauge-edit">
    <v-toolbar color="page-toolbar">
      <v-icon class="mr-8" @click="$router.push({ name: 'admin-gauge-list' })" title="Retour à la liste des stations">
        mdi-arrow-left
      </v-icon>
      <v-toolbar-title class="text-body-large" v-if="screen === 'edit'">
        Modifier une station <small>(Administration)</small>
      </v-toolbar-title>
      <v-toolbar-title class="text-body-large" v-else> Ajouter une station <small>(Administration)</small> </v-toolbar-title>
    </v-toolbar>
    <v-btn
      class="mt-n7 fab-absolute-right"
      icon
      size="large"
      color="error"
      @click="showDeleteConfirmation = true"
      v-if="screen === 'edit'"
    >
      <v-icon> mdi-delete </v-icon>
    </v-btn>
    <!-- Confirmation Dialog -->
    <v-dialog v-model="showDeleteConfirmation" persistent max-width="600px">
      <v-card>
        <v-card-title class="text-headline-small">Confirmer la suppression</v-card-title>
        <v-card-text> Êtes-vous sûr de vouloir supprimer cette station ? Cette action est irréversible. </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showDeleteConfirmation = false">Annuler</v-btn>
          <v-btn color="red-darken-1" variant="text" @click="confirmDelete">Confirmer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Page-level two-column layout: left = gauge info + water level, right = linked sections -->
    <v-row no-gutters class="pa-4">
      <!-- LEFT COLUMN -->
      <v-col cols="12" md="6" class="pr-2">
        <!-- Form for editing gauge details -->
        <v-card class="mb-4" variant="outlined" elevation="8">
          <v-card-title>Information sur la station</v-card-title>
          <v-card-text>
            <v-form>
              <v-row class="mb-3" no-gutters>
                <v-col>
                  <v-select v-model="gauge.type" label="Type" :items="gaugeTypes" required></v-select>
                  <!-- Show dependent selects if type is "Hydro-Quebec" -->
                  <div v-if="gauge.type === 'Hydro-Quebec'">
                    <v-select
                      v-model="selectedSource"
                      label="Source de données"
                      :items="hydroQuebecSources"
                      item-title="label"
                      item-value="url"
                      @update:model-value="fetchHydroQuebecData"
                      required
                    ></v-select>
                    <v-select
                      v-model="selectedRegion"
                      label="Région"
                      :items="hydroQuebecData.regionList"
                      item-title="RegionQC"
                      item-value="CodeRegionQC"
                      @update:model-value="filterNomList"
                      required
                    ></v-select>
                    <v-select
                      v-model="selectedNom"
                      label="Nom"
                      :items="filteredNomList"
                      item-title="Nom"
                      item-value="Nom"
                      @update:model-value="filterTypePointDonneeList"
                      required
                    ></v-select>
                    <v-select
                      v-model="selectedTypePointDonnee"
                      label="Type Point Donnée"
                      :items="filteredTypePointDonneeList"
                      item-title="TypePointDonnee"
                      item-value="TypePointDonnee"
                      @update:model-value="updateGaugeFields"
                      required
                    ></v-select>
                  </div>
                  <v-text-field
                    v-model="gauge.name"
                    label="Numéro de station"
                    :disabled="gauge.type === 'Hydro-Quebec'"
                    required
                  ></v-text-field>
                  <v-text-field
                    v-model="gauge.description"
                    label="Description"
                    :disabled="gauge.type === 'Hydro-Quebec'"
                    required
                  ></v-text-field>
                  <v-switch v-model="gauge.active" label="Actif" color="green-darken-3"></v-switch>

                  <v-btn variant="flat" color="primary" @click="updateGauge(gauge)" v-if="screen === 'edit'">
                    <v-icon>mdi-pencil</v-icon>
                    &nbsp; Modifier les informations de la station
                  </v-btn>
                  <v-btn variant="flat" color="primary" @click="addGauge(gauge)" v-if="screen === 'add'">
                    <v-icon>mdi-plus</v-icon>
                    &nbsp; Ajouter la station
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
        <v-card variant="outlined" elevation="8">
          <v-card-title>Information sur le niveau d'eau</v-card-title>
          <v-card-text>
            <v-btn
              variant="flat"
              color="primary"
              @click="viewWaterLevelHistory"
              :disabled="scrapeLoading || screen === 'add'"
            >
              <v-icon>mdi-gauge</v-icon> Voir l'historique des niveaux d'eau
            </v-btn>
            <v-row class="mb-3" no-gutters>
              <v-col md="6" v-if="scrapeSuccess && gauge.type === 'CEHQ'">
                <v-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Date</th>
                        <th class="text-left">Hour</th>
                        <th class="text-left">Level</th>
                        <th class="text-left">Flow</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in waterFlow.data.level" :key="item.date + item.hour">
                        <td>{{ item.date }}</td>
                        <td>{{ item.hour }}</td>
                        <td>{{ item.level }}</td>
                        <td>{{ item.flow }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-col>
              <v-col md="6" v-if="scrapeSuccess && gauge.type === 'Hydro-Quebec'">
                <v-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Date</th>
                        <th class="text-left">Hour</th>
                        <th class="text-left">Level</th>
                        <th class="text-left">Flow</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in waterFlow.data.level" :key="item.date + item.hour">
                        <td>{{ item.date }}</td>
                        <td>{{ item.hour }}</td>
                        <td>{{ item.level }}</td>
                        <td>{{ item.flow }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-col>
              <v-col md="6" v-if="scrapeSuccess && gauge.type === 'Hydro-Quebec'">
                <p>{{ waterFlow.data }}</p>
              </v-col>
              <v-col md="6" v-if="scrapeLoading">
                Veuillez patienter chargement des données en cours... Le chargement peut prendre quelques secondes
                puisque les données sont extraites d'un site externe à l'application.
              </v-col>
              <v-col md="6" v-if="!scrapeSuccess && !scrapeLoading && !scrapeError">
                Aucune donnée disponible pour cette station. Veuillez cliquer sur le bouton ci-dessus pour extraire les
                données.
              </v-col>
              <v-col md="6" v-if="scrapeError">
                Une erreur s'est produite lors de l'extraction des données. Veuillez réessayer plus tard.
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- RIGHT COLUMN: linked sections, sticky and independently scrollable -->
      <v-col cols="12" md="6" class="pl-2">
        <v-card variant="outlined" class="linked-sections-panel" elevation="8">
          <v-card-title>Sections utilisant cette station</v-card-title>
          <v-card-text>
            <div v-if="screen === 'add'" class="text-grey">
              Sauvegardez d'abord la station pour voir les sections associées.
            </div>
            <div v-else-if="linkedSectionsLoading">
              <v-progress-circular indeterminate color="primary" size="20" class="mr-2"></v-progress-circular>
              Chargement...
            </div>
            <div v-else-if="linkedSections.length === 0" class="text-grey">
              Aucune section n'utilise cette station.
            </div>
            <v-list v-else density="compact" class="linked-sections-list">
              <v-list-item
                v-for="section in linkedSections"
                :key="section.id"
                @click="$router.push({ name: 'admin-sections' })"
                class="px-0"
              >
                <template v-slot:prepend>
                  <v-icon size="small" class="mr-2">mdi-waves</v-icon>
                </template>
                <v-list-item-title>{{ section.river }} — {{ section.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ section.region }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import { GaugesService } from "../../../store/modules/gauges"; // Import the GaugesService from the store
  import SectionsService from "@/store/modules/sections";
  import { functions } from "@/firebase";
  import { notify, notifyError } from "@/services/Notification.service";
  export default {
    name: "GaugeEdit",
    data() {
      return {
        screen: "edit",
        gaugeId: this.$route.params.slug,
        gauge: { id: "", name: "", description: "", type: "", active: false },
        gaugeTypes: ["CEHQ", "Hydro-Quebec"], // Add the gauge types here
        showDeleteConfirmation: false,
        scrapeSuccess: false,
        scrapeLoading: false,
        scrapeError: false,
        waterFlow: [],
        hydroQuebecData: [], // Store Hydro-Quebec data
        hydroQuebecSources: [
          {
            label: "Débits et apports naturels aux installations",
            url: "https://www.hydroquebec.com/data/documents-donnees/donnees-ouvertes/json/Donnees_VUE_CENTRALES_ET_OUVRAGES.json",
          },
          {
            label: "Débits en rivière, niveaux d'eau et météo.",
            url: "https://www.hydroquebec.com/data/documents-donnees/donnees-ouvertes/json/Donnees_VUE_STATIONS_ET_TARAGES.json",
          },
        ],
        selectedSource: null,
        selectedRegion: null,
        selectedNom: null,
        selectedTypePointDonnee: null,
        filteredNomList: [],
        filteredTypePointDonneeList: [],
        linkedSections: [],
        linkedSectionsLoading: false,
      };
    },
    created() {
      if (this.gaugeId !== "new" && this.gaugeId !== undefined) {
        this.loadGaugeInfo();
        this.loadLinkedSections();
      } else {
        this.gauge = { id: "", name: "", description: "", type: "", active: true };
        this.screen = "add";
      }
    },
    watch: {
      "gauge.type"(newType) {
        if (newType !== "Hydro-Quebec") {
          this.selectedSource = null;
          this.hydroQuebecData = [];
          this.selectedRegion = null;
          this.selectedNom = null;
          this.selectedTypePointDonnee = null;
          this.filteredNomList = [];
          this.filteredTypePointDonneeList = [];
        }
      },
    },
    methods: {
      /*
      TODO
      Add a section to view gauge water level history

      */
      async loadGaugeInfo() {
        console.log("Loading gauge info for ID:", this.gaugeId);

        this.gauge = await this.getGaugeInfo();
        this.gauge.id = this.gaugeId;
        console.log("Gauge loaded:", this.gauge);

        // If it's a Hydro-Quebec gauge, restore the selections from the JSON
        if (this.gauge.type === "Hydro-Quebec") {
          await this.restoreHydroQuebecSelections();
        }
      },
      async restoreHydroQuebecSelections() {
        // Use the saved source, or default to the first one
        this.selectedSource = this.gauge.source || this.hydroQuebecSources[0].url;

        try {
          const response = await fetch(this.selectedSource);
          const jsonData = await response.json();
          const dataArray = jsonData.Site || jsonData.Station || [];

          if (!Array.isArray(dataArray)) {
            throw new Error("Expected an array but got something else.");
          }

          // Reconstruct the lists from the JSON
          const regionList = [];
          const nomList = [];
          const typePointDonneeList = [];

          dataArray.forEach((item) => {
            if (!regionList.some((region) => region.CodeRegionQC === item.CodeRegionQC)) {
              regionList.push({ RegionQC: item.RegionQC, CodeRegionQC: item.CodeRegionQC });
            }
            nomList.push({ id: item.identifiant, Nom: item.nom, CodeRegionQC: item.CodeRegionQC });
            if (Array.isArray(item.Composition)) {
              item.Composition.forEach((compositionItem) => {
                typePointDonneeList.push({ TypePointDonnee: compositionItem.type_point_donnee, Nom: item.nom });
              });
            }
          });

          this.hydroQuebecData = { regionList, nomList, typePointDonneeList };

          // Find the matching gauge in the data by identifiant (gauge.name)
          const matchingItem = dataArray.find((item) => item.identifiant === this.gauge.name);
          if (matchingItem) {
            this.selectedRegion = matchingItem.CodeRegionQC;
            this.filterNomList();

            this.selectedNom = matchingItem.nom;
            this.filterTypePointDonneeList();

            // Extract typePointDonnee from description (format: "region - nom - typePointDonnee")
            const descParts = this.gauge.description.split(" - ");
            this.selectedTypePointDonnee = descParts[2];
          }
        } catch (error) {
          console.error("Error restoring Hydro-Quebec selections:", error);
        }
      },
      async getGaugeInfo() {
        return await GaugesService.actions.getGauge(this.gaugeId);
      },
      async loadLinkedSections() {
        this.linkedSectionsLoading = true;
        this.linkedSections = await SectionsService.actions.findSectionsByGaugeId(this.gaugeId);
        this.linkedSectionsLoading = false;
      },
      async addGauge(gauge) {
        try {
          const createdGaugeId = await GaugesService.actions.addGauge(gauge);
          this.gaugeId = createdGaugeId;
          this.gauge.id = createdGaugeId;
          this.screen = "edit";
          this.loadLinkedSections();
          notify("La station a été ajoutée avec succès.");
        } catch (error) {
          console.error("Error adding gauge:", error);
          notifyError("La sauvegarde a échoué. Veuillez réessayer.");
        }
      },
      async updateGauge(gauge) {
        try {
          await GaugesService.actions.updateGauge(gauge);
          notify("La station a été enregistrée avec succès.");
        } catch (error) {
          console.error("Error updating gauge:", error);
          notifyError("La sauvegarde a échoué. Veuillez réessayer.");
        }
      },
      confirmDelete() {
        this.deleteGauge(this.gaugeId);
        this.showDeleteConfirmation = false; // Close the dialog after confirmation
        this.redirectToGaugeList();
      },
      async deleteGauge(gaugeId) {
        await GaugesService.actions.deleteGauge(gaugeId);
      },
      redirectToGaugeList() {
        this.$router.push({ name: "admin-gauge-list" });
      },
      async viewWaterLevelHistory() {
        this.scrapeError = false;
        this.scrapeSuccess = false;
        this.scrapeLoading = true;

        var viewWaterFlow = functions.httpsCallable("viewWaterFlow");
        const waterFlow = await viewWaterFlow({
          gaugeId: this.gaugeId,
          gaugeType: this.gauge.type,
          sourceUrl: this.gauge.type === "Hydro-Quebec" ? this.selectedSource : undefined,
        })
          .then((result) => result)
          .catch((error) => error);

        console.log("Water flow data:", waterFlow);
        this.scrapeLoading = false;

        if (waterFlow.data) {
          this.scrapeSuccess = true;
          this.waterFlow = waterFlow;
        } else {
          this.scrapeSuccess = false;
          this.scrapeError = true;
        }
      },
      async fetchHydroQuebecData() {
        if (!this.selectedSource) return;
        // Reset dependent selections when source changes
        this.selectedRegion = null;
        this.selectedNom = null;
        this.selectedTypePointDonnee = null;
        this.filteredNomList = [];
        this.filteredTypePointDonneeList = [];
        try {
          const response = await fetch(this.selectedSource);
          const jsonData = await response.json();
          // Ensure you access the correct property containing the array
          const dataArray = jsonData.Site || jsonData.Station || [];
          if (!Array.isArray(dataArray)) {
            throw new Error("Expected an array but got something else.");
          }

          // Extract the required lists
          const regionList = [];
          const nomList = [];
          const typePointDonneeList = [];

          dataArray.forEach((item) => {
            // Add to regionList if not already present
            if (!regionList.some((region) => region.CodeRegionQC === item.CodeRegionQC)) {
              regionList.push({ RegionQC: item.RegionQC, CodeRegionQC: item.CodeRegionQC });
            }

            // Add to nomList
            nomList.push({ id: item.identifiant, Nom: item.nom, CodeRegionQC: item.CodeRegionQC });

            // Add to typePointDonneeList by iterating over Composition
            if (Array.isArray(item.Composition)) {
              item.Composition.forEach((compositionItem) => {
                typePointDonneeList.push({ TypePointDonnee: compositionItem.type_point_donnee, Nom: item.nom });
              });
            }
          });

          // Store the lists in the component's data
          this.hydroQuebecData = {
            regionList,
            nomList,
            typePointDonneeList,
          };

          console.log("Hydro-Quebec data processed:", this.hydroQuebecData);
        } catch (error) {
          console.error("Error fetching Hydro-Quebec data:", error);
        }
      },
      filterNomList() {
        if (this.selectedRegion) {
          this.filteredNomList = this.hydroQuebecData.nomList.filter(
            (item) => item.CodeRegionQC === this.selectedRegion,
          );
          this.selectedNom = null; // Reset dependent selection
          this.filteredTypePointDonneeList = [];
          this.selectedTypePointDonnee = null;
        }
      },
      filterTypePointDonneeList() {
        if (this.selectedNom) {
          this.filteredTypePointDonneeList = this.hydroQuebecData.typePointDonneeList.filter(
            (item) => item.Nom === this.selectedNom,
          );
          this.selectedTypePointDonnee = null; // Reset dependent selection
        }
      },
      updateGaugeFields() {
        if (this.selectedRegion && this.selectedNom && this.selectedTypePointDonnee) {
          // Find the selected region name
          const region = this.hydroQuebecData.regionList.find(
            (item) => item.CodeRegionQC === this.selectedRegion,
          )?.RegionQC;

          // Find the selected nom's identifier
          const nom = this.hydroQuebecData.nomList.find((item) => item.Nom === this.selectedNom);

          // Update gauge fields
          this.gauge.name = nom?.id || ""; // Use the identifier from the nom list
          this.gauge.description = `${region} - ${this.selectedNom} - ${this.selectedTypePointDonnee}`;
        }
      },
      createTableFromData(_data) {
        // Create a table from the data
      },
    },
  };
</script>

<style scoped>
  .linked-sections-panel {
    position: sticky;
    top: 64px;
    max-height: calc(100vh - 80px);
    overflow-y: auto;
  }

  .linked-sections-list {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
</style>
